import { Box, CircularProgress } from '@mui/material'
import { useEffect, useRef, type ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import { useGetLojaAtualQuery } from '../api'
import { abrirLoja } from '../store/carrinho-slice'
import { armazenarTenant, ehPaginaPrincipal, fixarTenantDispositivo, tentarTenantDoHost, tenantVeioDoDispositivo } from '../lib/tenant'
import { fixarUrlDoAparelho } from '../lib/vinculo-aparelho'
import { ehDispositivoFixo, ehTotem, ehTotemCheckout } from '../lib/canal'
import { canalRastreioDoTipo, inicializarRastreamento, moduloDoTipo } from '../lib/tracking'
import { decidirTelaBootstrap, ehEnderecoInvalido, type ErroLojaAtual } from '../lib/tela-bootstrap'
import { useOperacaoCritica } from '../lib/operacao-critica'
import { LojaNaoEncontradaPage } from '../pages/loja-nao-encontrada'
import { ApresentacaoCardapioPage } from '../pages/apresentacao'
import { ModoIndisponivelPage } from '../pages/modo-indisponivel'
import { SemConexaoPage } from '../pages/sem-conexao'
import CheckoutKiosk from '../pages/totem/checkout'
import TotemPedidos from '../pages/totem/pedidos'

const RETENTATIVA_MS = 5_000

export function TenantBootstrap({ children }: { children: ReactNode }): JSX.Element {
  const dispatch = useDispatch()
  const { data, isLoading, isUninitialized, isFetching, error, refetch } = useGetLojaAtualQuery(undefined, { pollingInterval: 60_000 })
  const fezFallbackDeHost = useRef(false)
  const erro = error as ErroLojaAtual | undefined
  const carregando = isLoading || isUninitialized
  const operacaoCritica = useOperacaoCritica()
  const tela = decidirTelaBootstrap({ temDados: !!data, carregando, erro, operacaoCritica })
  const enderecoInvalido = !data && !!erro && ehEnderecoInvalido(erro)
  const semConexao = tela === 'sem-conexao'

  useEffect(() => {
    if (!enderecoInvalido || fezFallbackDeHost.current) return
    if (!tenantVeioDoDispositivo()) return

    if (!tentarTenantDoHost()) {
      console.warn('[tenant] código do aparelho não resolve e o endereço não tem loja (IP ou host sem domínio)')
      return
    }

    fezFallbackDeHost.current = true
    console.warn('[tenant] código gravado no aparelho não resolve, tentando o endereço da URL')
    void refetch()
  }, [enderecoInvalido, refetch])

  useEffect(() => {
    if (!semConexao) return

    const timer = window.setInterval(() => void refetch(), RETENTATIVA_MS)
    return () => window.clearInterval(timer)
  }, [semConexao, refetch])

  const lojaAberta = useRef<string | null>(null)

  useEffect(() => {
    if (!data) return

    const dispositivoFixo = ehDispositivoFixo(data.identificador)
    const desfazerVinculo = !dispositivoFixo && !fezFallbackDeHost.current

    if (dispositivoFixo || desfazerVinculo) fixarTenantDispositivo(dispositivoFixo ? data.identificador.codigo : null)

    if (fezFallbackDeHost.current) armazenarTenant(data.identificador.codigo.toLowerCase())
  
    if (dispositivoFixo) fixarUrlDoAparelho()

    const chaveDaLoja = `${data.configuracao.id}|${data.identificador.tipo}|${data.identificador.codigo}`

    if (lojaAberta.current !== chaveDaLoja) {
      lojaAberta.current = chaveDaLoja
      dispatch(abrirLoja({ idConfiguracaoCardapio: data.configuracao.id, volatil: dispositivoFixo }))
    }

    inicializarRastreamento({
      pixelFacebookId: data.configuracao.pixelFacebookId,
      googleAnalyticsId: data.configuracao.googleAnalyticsId,
      canalRastreio: canalRastreioDoTipo(data.identificador.tipo),
      modulo: moduloDoTipo(data.identificador.tipo),
      unidadeId: data.configuracao.idUnidade,
    })

    document.title = data.configuracao.nome
  }, [data, dispatch])

  if (tela === 'carregando') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  if (tela === 'modo-indisponivel') return <ModoIndisponivelPage mensagem={erro?.data?.mensagem} />
  if (tela === 'loja-nao-encontrada') return ehPaginaPrincipal() ? <ApresentacaoCardapioPage /> : <LojaNaoEncontradaPage />
  if (tela === 'sem-conexao' || !data) {
    return <SemConexaoPage aoTentarNovamente={() => void refetch()} tentando={isFetching} />
  }

  if (ehTotemCheckout(data.identificador)) {
    return <CheckoutKiosk />;
  }

  if (ehTotem(data.identificador)) return <TotemPedidos />

  return <>{children}</>
}
